import {isBrowser, normalizeStringList} from './utils';

function normalizeHostname(value: string): string | null {
  const candidate = value.trim().toLowerCase().replace(/^https?:\/\//, '').split('/')[0];
  if (!candidate || candidate.includes('@')) return null;
  return candidate.replace(/:\d+$/, '').replace(/^\.+|\.+$/g, '');
}

function matchesHostname(hostname: string, pattern: string): boolean {
  if (pattern.startsWith('*.')) {
    const suffix = pattern.slice(2);
    return hostname.length > suffix.length && hostname.endsWith(`.${suffix}`);
  }
  return hostname === pattern;
}

export class CrossDomainDecorator {
  private readonly domains: string[];
  private listening = false;

  constructor(
    domains: string[] | undefined,
    private readonly queryParameter: string,
    private readonly getClickId: () => string | null,
  ) {
    this.domains = normalizeStringList(domains)
      .map(normalizeHostname)
      .filter((value): value is string => value !== null);
  }

  start(): void {
    if (!isBrowser() || this.listening || !this.domains.length) return;
    document.addEventListener('pointerdown', this.handleEvent, true);
    document.addEventListener('click', this.handleEvent, true);
    document.addEventListener('auxclick', this.handleEvent, true);
    this.listening = true;
  }

  stop(): void {
    if (!isBrowser() || !this.listening) return;
    document.removeEventListener('pointerdown', this.handleEvent, true);
    document.removeEventListener('click', this.handleEvent, true);
    document.removeEventListener('auxclick', this.handleEvent, true);
    this.listening = false;
  }

  decorate(value: string | URL): string {
    if (!isBrowser()) return value.toString();
    let url: URL;
    try {
      url = new URL(value.toString(), window.location.href);
    } catch {
      return value.toString();
    }

    if (!['http:', 'https:'].includes(url.protocol)) return url.toString();
    if (url.origin === window.location.origin || !this.isAllowed(url.hostname)) return url.toString();
    if (url.searchParams.has(this.queryParameter)) return url.toString();

    const clickId = this.getClickId();
    if (clickId) url.searchParams.set(this.queryParameter, clickId);
    return url.toString();
  }

  private readonly handleEvent = (event: Event): void => {
    if (!(event.target instanceof Element)) return;
    const anchor = event.target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const decorated = this.decorate(anchor.href);
    if (decorated !== anchor.href) anchor.href = decorated;
  };

  private isAllowed(hostname: string): boolean {
    const normalized = hostname.toLowerCase().replace(/\.$/, '');
    return this.domains.some(domain => matchesHostname(normalized, domain));
  }
}
